import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import Card from "components/card";
import AccessButton from "components/button/accessbutton";
import { CATEGORY_ICONS } from "variables/constants"
import { Paperclip, Send } from "lucide-react";
import { Form } from "antd";
import FormItem from "components/fields/FormItem";
import { requiredField } from "utils/crudSagaHelper";
import TextArea from "antd/es/input/TextArea";
import Button from "components/button";
import {
    getTicketReplyList,
    doTicketReplyCreate,
} from "../../../../../redux/ticketreply/ticketreplyState";
import TicketReplyCard from "./TicketReplyCard";


const TicketDetailCard = ({ 
    ticket,
    recordList,
    getRecord,
    userId,
    doCreate,
    doSearch,
    pagination,
    filters,
    ticketId: propTicketId
 }) => {
    const [form] = Form.useForm();
    const repliesEndRef = useRef(null);
    const effectiveTicketId = propTicketId || ticket?.tic_id;

    const scrollToBottom = () => {
        repliesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        if (effectiveTicketId) {
            doSearch({
                ...(filters || {}),
                page: 1,
                paginate: pagination?.per_page || 10, 
                ticketId: effectiveTicketId,
            });
        }
    }, [doSearch, effectiveTicketId]);

    useEffect(() => {
        scrollToBottom();
    }, [recordList]);

    if (!ticket) return null


    const {
        tic_code: id,
        tic_title: title,
        submitted_by: submittedBy,
        ticket_priority: priority,
        ticket_status: status,
        ticket_category: category,
        tic_description: description,
        replies_count = recordList?.length,
    } = ticket;

    const Icon = CATEGORY_ICONS[ticket?.ticket_category];
    
    const onSubmit = (formData) => {
        try {
            if (!formData.trp_message?.trim()) return;

            formData.trp_ticket_id = ticket.tic_id;
            formData.trp_user_id = userId;
            formData.trp_is_internal = 0;
            formData.trp_status_id = 4;
            formData.trp_seen = 3;

            doCreate(formData);

            form.resetFields();
            getRecord({ ticketId: ticket.tic_id });
            scrollToBottom();
        } catch (error) {
            console.error("Reply failed:", error);
        }
    };
    

    return (
        <div className="space-y-6 max-w-[1400px] mx-auto">
            <AccessButton
                backLabel="Back to Tickets"
                withBack={true}
                baseRoute="/console/partner-service"
            />
            <Card className="bg-white text-slate-900 flex flex-col gap-6 rounded-xl border-2 border-purple-500 shadow-xl overflow-hidden">
                <div className="grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 pt-6 
                                grid-cols-[1fr_auto] [.border-b]:pb-6 bg-gradient-to-br from-purple-50 to-indigo-50 border-b"
                >
                    <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                            <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-500 text-white shadow-md">
                                {Icon && (
                                    <Icon className="w-6 h-6" />
                                )}
                            </div>
                            <div>
                                <h4 className="text-[20px] mb-1">{title}</h4>
                                <p className="text-slate-700 text-[13px]">
                                    Ticket ID: {id} • Submitted by {submittedBy}
                                </p>
                                <div className="flex items-center gap-2 mt-2">
                                    <span className={`status-indicator uppercase font-semibold ${priority.toLowerCase().replace(/\s+/g, "-")}`}>
                                        {priority}
                                    </span>
                                    <span className={`status-indicator uppercase font-semibold ${status.toLowerCase().replace(/\s+/g, "-")}`}>
                                        {status}
                                    </span>
                                    <span className={`status-indicator text-[10px] ${category.toLowerCase().replace(/\s+/g, "-")}`}>
                                        {category}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="pb-6 p-6">
                    <div className="space-y-6">
                        <div>
                            <h4 className="font-semibold text-[14px] mb-2">
                                Description
                            </h4>
                            <p className="text-[13px] text-slate-900">
                                {description}
                            </p>
                        </div>
                        <div className="bg-slate-200 shrink-0 h-px w-full"></div>
                        <div>
                            <h4 className="font-semibold text-[14px] mb-3">
                                Conversation ({replies_count})
                            </h4>
                            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                                {recordList?.map((reply) => (
                                   <TicketReplyCard 
                                        key={reply.trp_id} 
                                        {...reply} 
                                    />
                                ))}
                                <div ref={repliesEndRef} />
                            </div>
                            <div className="mt-4">
                                <Form
                                    form={form}
                                    layout="vertical" 
                                    onFinish={onSubmit}
                                >
                                    <FormItem
                                        name="trp_message"
                                        rules={[requiredField()]}
                                    >
                                        <TextArea
                                            id="trp_message"
                                            className="input-field"
                                            maxLength={255}
                                            placeholder="Type your response..."
                                            rows={2}
                                        />
                                    </FormItem>
                                    <div className="flex items-center justify-between">
                                        <Button
                                            className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all shrink-0 border bg-white text-slate-900 h-8 rounded-lg px-3 gap-2 hover:bg-slate-100 hover:text-slate-900 hover:shadow-none"
                                        >
                                            <Paperclip className="w-4 h-4" />
                                            Attach File
                                        </Button>
                                        <Form.Item shouldUpdate>
                                            {() => {
                                                const message = form.getFieldValue("trp_message");

                                                return (
                                                    <Button
                                                        htmlType="submit"
                                                        disabled={!message?.trim()}
                                                        className={`inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-all shrink-0 h-9 px-4 py-2 gap-2 hover:shadow-none ${
                                                            message?.trim()
                                                                ? "bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white"
                                                                : "bg-gradient-to-r from-purple-600 to-indigo-600 text-white opacity-50"
                                                        }`}
                                                    >
                                                        <Send className="w-4 h-4" />
                                                        Send Reply
                                                    </Button>
                                                );
                                            }}
                                        </Form.Item>
                                    </div>
                                </Form>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    );
}
const mapStateToProps = (state) => ({
    recordList: state.ticketReply.ticketReplyList?.items,
    requesting: state.ticketReply.ticketReplyList?.requesting,
    filters: state.ticketReply.ticketReplyList?.filters,
    success: state.ticketReply.ticketReplyList?.success,
    userId: state.auth?.user?.id,
});

const mapDispatchToProps = (dispatch) => ({
    doSearch: (filter) => dispatch(getTicketReplyList(filter)),
    doCreate: (data) => dispatch(doTicketReplyCreate(data)),
});
export default connect(mapStateToProps, mapDispatchToProps)(TicketDetailCard);